import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CardSupportMobile from '@/components/CardSupportMobile/CardSupportMobile';
import ChipsRadio from '@/components/chipsRadio/chipsRadio';
import DemoTag from '@/components/DemoTag/DemoTag';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { RowSupport } from '@/components/RowSupport/rowSupport';
import { useDemoContext } from '@/hooks/DemoContext';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ContentModalSupportInfo } from '@/modals/ContentModalSupportInfo/ContentModalSupportInfo';
import { DetailsRepartitionResponse, Support } from '@/services/types/commonResponse';

import styles from './styles.module.css';

const ordrePersonnalise = ['Libre', 'Pilotée', 'Pilotée A Horizon', 'Tous modes de gestion'];

const comparerModesGestion = (a: string, b: string) => {
  const indexA = ordrePersonnalise.indexOf(a);
  const indexB = ordrePersonnalise.indexOf(b);
  const safeIndexA = indexA === -1 ? ordrePersonnalise.length : indexA;
  const safeIndexB = indexB === -1 ? ordrePersonnalise.length : indexB;
  if (safeIndexA !== safeIndexB) return safeIndexA - safeIndexB;
  if (indexA === -1 && indexB === -1) return a.localeCompare(b);
  return 0;
};

const getModeGestionLabel = (mode: string) => {
  if (mode === 'Tous modes de gestion') return 'Tous modes de gestion';
  if (mode === 'Pilotée A Horizon') return 'Gestion Pilotée à Horizon Retraite';
  return `Gestion ${mode}`;
};

interface SupportsProps {
  dataSupports: DetailsRepartitionResponse;
}

export const SupportsDetails: React.FC<SupportsProps> = ({ dataSupports }) => {
  const { t } = useTranslation();
  const { isDemo } = useDemoContext();
  const [showFilter, setShowFilter] = useState(false);
  const [filterSupport, setFilterSupport] = useState('');
  const { windowWidth } = useScreenSize();
  const [showModalSupport, setShowModalSupport] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState<Support | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const modesGestionDisponibles = [
    ...new Set(dataSupports?.supports?.map((mode) => mode.modeGestionLibelle) ?? []),
    'Tous modes de gestion',
  ];

  const viewportRef = useRef<HTMLDivElement | null>(null);

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const elem = document.getElementById('filter-mode-gestion-id');
    const content = document.getElementById('content');

    if (elem?.offsetWidth !== content?.offsetWidth) return;

    if (!viewportRef.current || e.deltaY === 0 || e.deltaX !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY;
    const currPos = viewportRef.current.scrollLeft;
    const scrollWidth = viewportRef.current.scrollWidth;

    const newPos = Math.max(0, Math.min(scrollWidth, currPos + delta));

    viewportRef.current.scrollLeft = newPos;
  };

  useEffect(() => {
    viewportRef.current?.addEventListener('wheel', (e: WheelEvent) => {
      onWheel(e as unknown as React.WheelEvent<HTMLDivElement>);
    });
  }, []);
  // Fin gestion scroll horizontal

  useEffect(() => {
    if (!isCopied) return;
    const timeoutId = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  useEffect(() => {
    if (modesGestionDisponibles.length) {
      setFilterSupport(modesGestionDisponibles.length > 2 ? '' : modesGestionDisponibles[0]);
      setShowFilter(modesGestionDisponibles.length > 2);
    }
  }, [dataSupports?.supports, modesGestionDisponibles]);

  const filteredSupports = (dataSupports?.supports ?? [])
    .slice()
    .filter(Boolean)
    .sort((a, b) => (b?.allocation ?? 0) - (a?.allocation ?? 0))
    .filter((support: Support) => {
      if (!filterSupport) return true;
      return [filterSupport].includes(support?.modeGestionLibelle);
    });

  const openModal = (index: number) => {
    const support = filteredSupports[index];
    if (!support) return;
    document.body.classList.add('modal-open');
    setSelectedSupport(support);
    setShowModalSupport(true);
  };

  const closeModal = () => {
    setIsCopied(false);

    setShowModalSupport(false);
  };

  const date = new Date();
  const dateOuverture = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}/${date.getFullYear()}`;

  const copyCode = async (isin: string = '') => {
    try {
      await navigator.clipboard.writeText(isin);
    } catch {
      const tempInput = document.createElement('input');
      tempInput.value = isin;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy'); // NOSONAR typescript:S1874 -- intentional fallback for browsers without Clipboard API support
      tempInput.remove();
    }
    setIsCopied(true);
  };

  return (
    <>
      {showModalSupport && (
        <ModaleDefaut
          title=""
          content={
            <ContentModalSupportInfo
              support={selectedSupport!}
              copyCode={copyCode}
              isCopied={isCopied}
              dateOuverture={dateOuverture}
            />
          }
          btnLabel="Fermer"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => {
            closeModal();
            void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
              event_id: 'clic',
              event_categorie: 'clic',
              event_metier: 'espace client placement',
              event_nom: 'fermeture pop in support',
              event_detail_1: 'cta',
              event_detail_2: 'pop in support',
            });
          }}
          btnFullwidth={false}
          onClickClose={() => {
            closeModal();
            void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
              event_id: 'clic',
              event_categorie: 'clic',
              event_metier: 'espace client placement',
              event_nom: 'faire un versement',
              event_detail_1: 'picto',
            });
          }}
          mobileStyle="stick-to-bottom"
          size="medium"
        />
      )}

      {modesGestionDisponibles.length > 2 && (
        <div className={styles.filterManagementMode} ref={viewportRef} id="filter-mode-gestion-id">
          {modesGestionDisponibles
            .slice()
            .sort(comparerModesGestion)
            .map((mode, index) => (
              <ChipsRadio
                key={mode}
                name="filterModeGestion"
                size="medium"
                isChecked={index === modesGestionDisponibles.length - 1}
                value={mode}
                label={getModeGestionLabel(mode)}
                onClick={() => {
                  setFilterSupport(mode === 'Tous modes de gestion' ? '' : mode);
                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                    event_id: 'clic',
                    event_categorie: 'clic',
                    event_metier: 'espace client placement',
                    event_nom: mode === 'Tous modes de gestion' ? 'tous modes de gestion' : 'gestion ' + mode,
                    event_detail_1: 'filtre',
                  });
                }}
              />
            ))}
        </div>
      )}
      {windowWidth > 599 ? (
        <table id="supports-table" className={styles.supportsTable}>
          <thead>
            <tr className={styles.tableRow}>
              <th className={`${styles.tableHeader} ${isDemo ? styles.tableHeaderDemo : ''}`}>
                {t('supports.supportNameLabel')}
                {isDemo && <DemoTag />}
              </th>
              {windowWidth > 1200 && (
                <>
                  <th className={styles.tableHeader}>{t('supports.quantityLabel')}</th>
                  <th className={styles.tableHeader}>{t('supports.unitCostPriceLabel')}</th>
                  <th className={styles.tableHeader}>{t('supports.currentPriceLabel')}</th>
                </>
              )}
              <th className={styles.tableHeader}>{t('supports.valueLabel')}</th>
              <th className={styles.tableHeader}>{t('supports.valueChangeLabel')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSupports?.length > 0 &&
              filteredSupports.map((support: Support, index: number) => {
                return (
                  <RowSupport
                    key={support.supportId}
                    index={index}
                    openModal={openModal}
                    support={support}
                    windowWidth={windowWidth}
                    showFilter={showFilter}
                  />
                );
              })}
          </tbody>
        </table>
      ) : (
        <div className={styles.mobileSupportCards}>
          {isDemo && <DemoTag />}
          {filteredSupports?.length > 0 &&
            filteredSupports.map((support: Support, index: number) => {
              return (
                <CardSupportMobile
                  key={support.supportId}
                  showFilter={showFilter}
                  index={index}
                  openModal={openModal}
                  support={support}
                />
              );
            })}
        </div>
      )}
    </>
  );
};
